import { MouseEventHandler, ReactNode, useContext } from 'react';
import { useId } from '@floating-ui/react-dom-interactions';
import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { Placement } from '../../../hint/get-position';
import { Truncated } from '../../../truncated';
import { SelectContext } from './dropdown-field.context';
import { DropdownFieldStyles } from './styles';

const dropdownListItemDivStyle2 = css`
  position: absolute;
  left: 0px;
  top: 0px;
  width: 2px;
  height: 100%;
  background-color: ${theme.colors.primary50};
`;

const dropdownListItemStyle1 = css`
  position: relative;
`;

export interface DropdownListItemProps {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  isSelectable?: boolean;
  tooltipPlacement?: Placement;
  /** String for searching against (if children is jsx or varies from the value) */
  searchValue?: string;
  trackingId?: string;
  displayValue?: string;
  value: string;
  onClick?: MouseEventHandler;
}

/**
 *
 * After some of the new floating-ui components have baked and garnered trust,
 * this component should be genericized and merged with
 * `libs/ui/design-system/src/components/forms/fields/time-field/dropdown-list-item.tsx`
 */
export const DropdownListItem = ({
  children,
  className,
  disabled,
  isSelectable = true,
  searchValue,
  value,
  displayValue,
  tooltipPlacement,
  onClick,
  trackingId,
  ...rest
}: DropdownListItemProps) => {
  const {
    activeIndex,
    getItemProps,
    listItemsRef,
    lookupByValue,
    name,
    onChange,
    selectedIndex,
    setFloatingElementOpen,
    setSelectedIndex,
  } = useContext(SelectContext);
  const index = lookupByValue[value]?.index ?? 0;
  const id = useId();
  const selected = selectedIndex === index;
  const active = activeIndex === index;
  const dropdownOptionStyles = DropdownFieldStyles.dropdownOption({ active, disabled });

  const { key, ...itemProps } = getItemProps({
    ref: (node: HTMLLIElement) => {
      if (node) {
        listItemsRef.current[index] = node;
      }
    },
    // order of items will not change, so index is a reliable key
    key: index,
    onClick(e) {
      if (disabled) return;
      setSelectedIndex(index);
      onChange({ name, value });

      setFloatingElementOpen(false);

      if (typeof onClick === 'function') {
        onClick(e);
      }
    },
  });

  return (
    <li
      aria-disabled={disabled}
      aria-selected={active}
      id={id}
      role='option'
      data-value={value}
      data-trackingid={trackingId}
      {...itemProps}
      {...rest}
      className={cx(dropdownOptionStyles, dropdownListItemStyle1, className)}
    >
      {isSelectable && selected && <div data-testid='select-option-indicator' className={dropdownListItemDivStyle2} />}
      <Truncated as='span' className='select-option-text' tooltipOptions={{ placement: tooltipPlacement }}>
        {children ?? <i>No value</i>}
      </Truncated>
    </li>
  );
};
